import React, { useState, useEffect } from 'react';
import { adminApi } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { Check, X, Star, BadgeCheck } from 'lucide-react';

const STATUSES = [
  { value: '', label: 'Tutte' },
  { value: 'pending', label: 'In attesa' },
  { value: 'approved', label: 'Approvate' },
  { value: 'rejected', label: 'Rifiutate' },
];

const statusColor = (status) => {
  switch (status) {
    case 'approved': return 'bg-[#4A5D23] text-white';
    case 'rejected': return 'bg-[#C05746] text-white';
    default: return 'bg-[#E5E0D8] text-[#57534E]';
  }
};

export default function AdminReviewsPage() {
  const { toast } = useToast();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  useEffect(() => {
    fetchReviews();
  }, [filter]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getReviews(filter || undefined);
      setReviews(response.data.reviews);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (reviewId, status) => {
    try {
      await adminApi.updateReviewStatus(reviewId, status);
      toast({ title: status === 'approved' ? 'Recensione approvata' : 'Recensione rifiutata' });
      fetchReviews();
    } catch (error) {
      toast({ title: 'Errore', description: error.response?.data?.detail, variant: 'destructive' });
    }
  };

  return (
    <div>
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <h2 className="text-2xl font-light">Moderazione Recensioni</h2>
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((status) => (
            <button
              key={status.value}
              onClick={() => setFilter(status.value)}
              data-testid={`filter-review-${status.value || 'all'}`}
              className={`px-4 py-2 text-sm transition-colors ${
                filter === status.value ? 'bg-[#1C1917] text-white' : 'bg-[#F2EFE9] hover:bg-[#E5E0D8]'
              }`}
            >
              {status.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-[#E5E0D8] animate-pulse" />
          ))}
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-16 bg-[#F2EFE9]">
          <p className="text-[#57534E]">Nessuna recensione trovata</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review, idx) => (
            <div key={review.review_id} className="bg-[#F2EFE9] p-6" data-testid={`admin-review-${idx}`}>
              <div className="flex flex-wrap gap-6">
                {review.product_image && (
                  <img src={review.product_image} alt="" className="w-20 h-28 object-cover flex-shrink-0" />
                )}
                <div className="flex-1 min-w-[240px]">
                  <p className="font-medium">{review.product_name || review.product_id}</p>
                  <div className="flex flex-wrap items-center gap-3 mt-2">
                    <span className="inline-flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={i} className={`w-4 h-4 ${i <= review.rating ? 'fill-[#B8860B] text-[#B8860B]' : 'text-[#D6D3CE]'}`} />
                      ))}
                    </span>
                    <span className="text-sm">{review.customer_name}</span>
                    {review.verified && (
                      <span className="inline-flex items-center gap-1 text-xs text-[#4A5D23]">
                        <BadgeCheck className="w-4 h-4" />
                        Acquisto verificato
                      </span>
                    )}
                    <span className="text-xs text-[#78716C]">
                      {new Date(review.created_at).toLocaleDateString('it-IT')}
                    </span>
                  </div>
                  <p className="font-medium mt-3">{review.title}</p>
                  <p className="text-sm text-[#57534E] mt-1">{review.text}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span className={`px-3 py-1 text-sm ${statusColor(review.status)}`} data-testid={`review-status-${idx}`}>
                    {{ pending: 'In attesa', approved: 'Approvata', rejected: 'Rifiutata' }[review.status] || review.status}
                  </span>
                  {review.status === 'pending' && (
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => handleStatusChange(review.review_id, 'approved')}
                        data-testid={`approve-review-${idx}`}
                        className="flex items-center gap-1 px-4 py-2 bg-[#4A5D23] text-white hover:bg-[#3B4A1C] transition-colors"
                      >
                        <Check className="w-4 h-4" />
                        Approva
                      </button>
                      <button
                        onClick={() => handleStatusChange(review.review_id, 'rejected')}
                        data-testid={`reject-review-${idx}`}
                        className="flex items-center gap-1 px-4 py-2 bg-[#C05746] text-white hover:bg-[#a84637] transition-colors"
                      >
                        <X className="w-4 h-4" />
                        Rifiuta
                      </button>
                    </div>
                  )}
                  {review.status !== 'pending' && (
                    <button
                      onClick={() => handleStatusChange(review.review_id, review.status === 'approved' ? 'rejected' : 'approved')}
                      data-testid={`toggle-review-${idx}`}
                      className="px-4 py-2 text-sm bg-white border border-[#E5E0D8] hover:border-[#1C1917] transition-colors mt-2"
                    >
                      {review.status === 'approved' ? 'Rimuovi dal sito' : 'Approva'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
